import { useState } from 'react';
import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import type { Typeahead } from '../../hooks/useTypeahead';
import type { FieldBaseProps } from './types';

export interface AutocompleteOption {
  value: string;
  label: string;
}

/** See `UserPicker`'s `useStableOption` — MUI compares `value` by reference. */
function useStableOption(option: AutocompleteOption | null): AutocompleteOption | null {
  const [stable, setStable] = useState(option);
  const changed =
    (stable?.value ?? null) !== (option?.value ?? null) ||
    (stable?.label ?? null) !== (option?.label ?? null);
  if (changed) setStable(option);
  return changed ? option : stable;
}

export interface FieldAutocompleteProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** The searchable option set. */
  options: AutocompleteOption[];
  placeholder?: string;
  /** Message when no options match / none are available (translated copy). */
  noOptionsText?: ReactNode;
  loading?: boolean;
  loadingText?: ReactNode;
  /**
   * The caller's {@link Typeahead}. Passing it switches **off** client-side
   * filtering (`options` is the server's answer for the current term, BUG-034)
   * and hands MUI a controlled input box so a typed term survives a blur.
   */
  search?: Typeahead;
  /**
   * True while no page answers the term in the box — from
   * `typeaheadPage(...).awaiting`. Renders the loading text instead of the
   * previous term's rows (BUG-043).
   */
  awaiting?: boolean;
  /**
   * The resolved option for the field's current value when it is not in
   * `options` — with a paged/searched read the stored value is often off-page
   * (editing a user whose manager sorts past the first page), and without this
   * the field would render empty and a blind save would drop the value.
   */
  selectedOption?: AutocompleteOption | null;
}

/**
 * RHF-bound searchable single-select on MUI `Autocomplete`. Stores the selected
 * option's `value` (a string id) in the form. Used for the add-device
 * master-device picker (type to search a long master catalog) and the user
 * form's manager picker; the kit's `FieldSelect` is a plain dropdown with no
 * filtering, so this fills the gap.
 */
export function FieldAutocomplete<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  required,
  disabled,
  options,
  placeholder,
  noOptionsText,
  loading,
  loadingText,
  search,
  awaiting = false,
  selectedOption,
}: FieldAutocompleteProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        // A separate component, not an inline render body: `AutocompleteControl`
        // owns a hook (`useStableOption`), and a hook inside a render prop is not
        // a stable component boundary.
        <AutocompleteControl
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          errorMessage={fieldState.error?.message}
          label={label}
          helperText={helperText}
          required={required}
          disabled={disabled}
          options={options}
          placeholder={placeholder}
          noOptionsText={noOptionsText}
          loading={loading}
          loadingText={loadingText}
          search={search}
          awaiting={awaiting}
          selectedOption={selectedOption}
        />
      )}
    />
  );
}

interface AutocompleteControlProps {
  value: string;
  onChange: (next: string) => void;
  onBlur: () => void;
  errorMessage?: string;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  options: AutocompleteOption[];
  placeholder?: string;
  noOptionsText?: ReactNode;
  loading?: boolean;
  loadingText?: ReactNode;
  search?: Typeahead;
  awaiting?: boolean;
  selectedOption?: AutocompleteOption | null;
}

function AutocompleteControl({
  value,
  onChange,
  onBlur,
  errorMessage,
  label,
  helperText,
  required,
  disabled,
  options,
  placeholder,
  noOptionsText,
  loading,
  loadingText,
  search,
  awaiting = false,
  selectedOption,
}: AutocompleteControlProps) {
  const serverSearched = !!search;
  const match =
    options.find((o) => o.value === value) ??
    (selectedOption && selectedOption.value === value ? selectedOption : null);
  // Reference-stable, because MUI compares `value` by reference to decide whether
  // to rewrite the input box — see useStableOption.
  const selected = useStableOption(match);
  // Keep the selection in the list even when the current page lacks it, or
  // MUI warns that the value matches no option.
  const renderedOptions =
    selected && !options.some((o) => o.value === selected.value)
      ? [selected, ...options]
      : options;

  return (
    <Autocomplete<AutocompleteOption>
      options={renderedOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, v) => option.value === v.value}
      value={selected}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      // While awaiting, render nothing rather than the page fetched for an
      // older term; the selection stays in `options` for value matching.
      filterOptions={serverSearched ? (opts) => (awaiting ? [] : opts) : undefined}
      onChange={(_, next) => onChange(next ? next.value : '')}
      {...(search
        ? { inputValue: search.inputValue, onInputChange: search.onInputChange }
        : {})}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!errorMessage}
          helperText={errorMessage ?? helperText}
        />
      )}
    />
  );
}
